import * as actionTypes from 'modules/auditModels/actionTypes';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {{
 *      isLoading: boolean,
 *      apiError: string,
 *      ids: Array<string>,
 *      page: import('modules/global/generic.types').Page,
 *      filters: import('modules/auditModels/types').AuditModelsSelectFilters,
 * }} SliceSelect
 *
 * @typedef {{
 *     [token:string] : SliceSelect,
 * }} State
 */

/** @type {State} */
const initialState = {};

/** @type {SliceSelect} */
export const emptySlice = {
  isLoading: false,
  apiError: null,
  ids: [],
  page: { number: 0, totalElements: 0, totalPages: 0, size: 10 },
  filters: {
    status: ['DRAFT', 'OPEN'],
  },
};

/**
 * @param {State} state
 * @param {import('modules/auditModels/types').InitializeAuditModelSelectorAction} action
 * @return {State}
 */
const initAuditModelsSelect = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...emptySlice,
    ...state[payload.token],
    filters: {
      ...emptySlice.filters,
      ...payload.params,
    },
  },
});

/**
 * @param {State} state
 * @param {import('modules/auditModels/types').GetAuditModelsSelectAction} action
 * @returns {State}
 */
const beforeFetchAuditModels = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: true,
    apiError: null,
  },
});

/**
 * @param {State} state
 * @param {import('modules/auditModels/types').GetAuditModelsSelectFailureAction} action
 * @returns {State}
 */
const hasError = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: false,
    apiError: payload.message,
  },
});

/**
 * @param {State} state
 * @param {import('modules/auditModels/types').GetAuditModelsSelectSuccessAction} action
 * @returns {State}
 */
const updateAuditModelsSelect = (state, { payload, meta = {} }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: false,
    apiError: null,
    page: !meta.shouldUpdate ? payload.page : state[payload.token].page,
    ids: uniq(
      payload.content.reduce(
        (acc, auditModel) => {
          const auditModelId = auditModel.auditModelId ?? auditModel.id;
          return [...acc, auditModelId];
        },
        !meta.shouldUpdate && payload.page.number === 0 ? [] : state[payload.token].ids
      )
    ),
  },
});

/**
 * @param {State} state
 * @param {import('modules/auditModels/types').setAuditModelSelectFilters} action
 * @returns {State}
 */
const setFilters = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    filters: {
      ...state[payload.token].filters,
      ...payload.filters,
    },
  },
});

export default createReducer(initialState, {
  [actionTypes.INIT_AUDIT_MODELS_SELECT]: initAuditModelsSelect,
  [actionTypes.GET_AUDIT_MODELS_SELECT_FETCH]: beforeFetchAuditModels,
  [actionTypes.GET_AUDIT_MODELS_SELECT_FETCH_FAILURE]: hasError,
  [actionTypes.GET_AUDIT_MODELS_SELECT_FETCH_SUCCESS]: updateAuditModelsSelect,
  [actionTypes.SET_AUDIT_MODELS_SELECT_FILTERS]: setFilters,
});
